import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from 'react'
import { useTranslation } from 'next-i18next'
import {
  Text,
  TextLink,
  useDialogControllers,
  useToast
} from '@machobear/design-system'

import useCooldown from '@/hooks/use-cooldown'
import useCurrentSession from '@/hooks/use-current-session'
import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import { useMessageChannel } from '@/hooks/use-message-channel'
import { useShowLandlordErrorToast } from '@/hooks/use-show-landlord-error-toast'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import DSMessageModal from '@/components/listing-card/ds-message-modal'
import { Listing } from '@/components/listing-card/types'
import { useTermsDialogContext } from '@/components/terms-dialog/context'

export const AUTOSEND_START = 'autosend_start'
export const AUTOSEND_END = 'autosend_end'

type ContextType = {
  currentListing: Listing | null
  isAutoSendingMessage: boolean
  countDownStarted: boolean
  broadcast: (data: { type: string; payload: any }) => void
  showMessageModal: (listing: Listing) => void
  showInquiryUndoToast: (params: {
    timeout: number
    onUndo: () => void
    onTimeout: () => void
  }) => () => void
  setIsAutoSendingMessage: (val: boolean) => void
  setCountDownStarted: (val: boolean) => void
  setCurrentListing: (listing: Listing) => void
  unsetCurrentListing: () => void
  showAutosendMessageModal: (
    listing: Listing,
    isCardLocked: boolean,
    isLandlord: boolean
  ) => void
}

const MessageModalContext = createContext<ContextType | undefined>(undefined)
interface MessageModalProviderProps {
  children: ReactNode
}

export const MessageModalProvider: React.FC<MessageModalProviderProps> = ({
  children
}) => {
  const { t } = useTranslation()
  const { visible, showModal, closeModal } = useDialogControllers({
    dsMessageModal: false,
    login: false
  })
  const [listingData, setListingData] = useState<Listing | null>(null)
  const session = useCurrentSession()
  const showLandlordErrorToast = useShowLandlordErrorToast()
  const { showModalForRequiredTerms } = useTermsDialogContext()
  const [isAutoSendingMessage, setIsAutoSendingMessage] = useState(false)
  const [countDownStarted, setCountDownStarted] = useState(false)
  const { startCooldown, resetCooldown } = useCooldown()
  const showToast = useToast()
  const ensureLoggedIn = useEnsureLoggedIn({
    showLogin: () => showModal('login')
  })

  const { broadcast } = useMessageChannel({
    channelName: 'ListingCardActions',
    onMessage: data => {
      if (data.type === AUTOSEND_START) {
        setCurrentListing(data.payload.listing)
        setCountDownStarted(true)
        setIsAutoSendingMessage(true)
      } else if (data.type === AUTOSEND_END) {
        unsetCurrentListing()
        setCountDownStarted(false)
        setIsAutoSendingMessage(false)
      }
    }
  })

  const showMessageModal = (listing: Listing) => {
    setListingData(listing)
    showModal('dsMessageModal')()
  }

  const showAutosendMessageModal = (
    listing: Listing,
    isCardLocked: boolean,
    isLandlord: boolean
  ) => {
    setListingData(listing)
    if (isLandlord) {
      return showLandlordErrorToast()
    }

    if (isCardLocked) {
      ensureLoggedIn(() =>
        showModalForRequiredTerms('trreb_agreements', () =>
          showModal('dsMessageModal')()
        )
      )
      return
    }
    showModal('dsMessageModal')()
  }

  const setCurrentListing = (listing: Listing) => {
    setListingData(listing)
  }

  const unsetCurrentListing = () => {
    setListingData(null)
  }

  const showInquiryUndoToast = useCallback(
    (params: {
      timeout: number
      onUndo: () => void
      onTimeout: () => void
    }) => {
      startCooldown(params.timeout, params.onTimeout)

      const { removeToast } = showToast({
        tone: 'positive' as const,
        message: t('Sending Message...'),
        description: (
          <Text>
            <TextLink
              href={''}
              onClick={e => {
                e.preventDefault()
                if (!isAutoSendingMessage) {
                  resetCooldown()
                  params.onUndo()
                }
              }}
            >
              {t('Undo')}
            </TextLink>
          </Text>
        )
      })

      return removeToast
    },
    [startCooldown, showToast, t, resetCooldown, isAutoSendingMessage]
  )

  return (
    <MessageModalContext.Provider
      value={{
        currentListing: listingData,
        countDownStarted,
        isAutoSendingMessage,
        broadcast,
        showInquiryUndoToast,
        unsetCurrentListing,
        setCountDownStarted,
        setCurrentListing,
        setIsAutoSendingMessage,
        showMessageModal,
        showAutosendMessageModal
      }}
    >
      {listingData && (
        <DSMessageModal
          visible={visible.dsMessageModal}
          onClose={() => closeModal('dsMessageModal')()}
          listing={listingData}
        />
      )}
      {children}
      <DSLoginModal
        mode="login"
        eventId={'web-signup-submit-savebutton'}
        visible={visible.login}
        onClose={closeModal('login')}
        t={t}
        listingId={parseInt(listingData?.listing_id ?? listingData?.id ?? '')}
      />
    </MessageModalContext.Provider>
  )
}

export const useMessageModalContext = (): ContextType => {
  const context = useContext(MessageModalContext)
  if (!context) {
    throw new Error(
      'useMessageModalContext must be used within a MessageModalProvider'
    )
  }
  return context
}
